// BroadcastComposer (issue #422, server preview wired in #449). Modal that
// lets a lead/admin compose a broadcast notice to assigned contractors.
// Submission is two-step:
//
//   1. The user fills in title/body/target. On "Next: Review" the modal
//      switches to a confirmation view.
//   2. The confirmation view calls `useBroadcastPreview` to fetch the
//      server-resolved recipient count, then renders "Send to N
//      contractors". The user confirms; the mutation fires and the modal
//      closes.
//
// Recipient count: the count is read from
// `GET /chats/jobs/:jobId/broadcasts/preview` so it shares the resolver
// with the eventual `POST` — the preview can never disagree with what
// the server actually delivers to. The `roster` prop kept here is unused
// for the count (the legacy client-side `previewRecipientCount` helper is
// no longer wired into this flow), but stays in the prop signature for
// callers that still pass it; it can be dropped once every call site is
// audited.
//
// Attachments: the issue notes that attachments must be uploaded against
// the broadcast chat *after* it exists, but the current backend creates
// the broadcast chat only inside `send`. For Sprint 4 the modal limits
// attachments to an inline "drop file here" placeholder list and submits
// `attachmentIds = []` — a follow-up issue is filed against #422 to wire
// the attachment workflow once the backend exposes a "create broadcast
// chat (idempotent, no message)" endpoint.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import {
  BroadcastRosterRow,
  useBroadcastPreview,
  useSendBroadcast,
} from 'src/queries/chat';

import type { BroadcastTarget } from 'src/models/chat';

const validationSchema = Yup.object({
  title: Yup.string()
    .trim()
    .required('Title is required')
    .max(80, 'Title must be 80 characters or fewer'),
  body: Yup.string()
    .trim()
    .required('Message is required')
    .max(2000, 'Message must be 2000 characters or fewer'),
  target: Yup.mixed<BroadcastTarget>()
    .oneOf([
      'ALL_ASSIGNED',
      'CONFIRMED_ONLY',
      'NOT_CHECKED_IN',
      'CUSTOM_USER_IDS',
    ])
    .required(),
  customUserIds: Yup.array().of(Yup.number()),
  replyBehavior: Yup.mixed<'READ_ONLY' | 'REPLY_PRIVATELY_TO_LEAD'>()
    .oneOf(['READ_ONLY', 'REPLY_PRIVATELY_TO_LEAD'])
    .required(),
});

interface FormValues {
  title: string;
  body: string;
  target: BroadcastTarget;
  customUserIds: number[];
  replyBehavior: 'READ_ONLY' | 'REPLY_PRIVATELY_TO_LEAD';
}

interface ComposerDraft {
  jobId: number;
  shiftId?: number;
  values: FormValues;
}

const DEFAULT_VALUES: FormValues = {
  title: '',
  body: '',
  target: 'ALL_ASSIGNED',
  customUserIds: [],
  replyBehavior: 'READ_ONLY',
};

interface BroadcastComposerProps {
  open: boolean;
  onClose: () => void;
  jobId: number;
  /** Optional shift context; surfaced in the modal header. */
  shiftId?: number;
  /**
   * Caller-supplied roster. Kept on the prop signature for backward
   * compatibility with the job-detail panel; the recipient count is now
   * resolved server-side via `useBroadcastPreview` (issue #449), so this
   * prop is no longer read by the composer. Will be removed in a
   * follow-up once every call site is audited.
   */
  roster?: ReadonlyArray<BroadcastRosterRow>;
}

export default function BroadcastComposer({
  open,
  onClose,
  jobId,
  shiftId,
}: BroadcastComposerProps) {
  const sendMutation = useSendBroadcast();
  const requestKey = useRef<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [draft, setDraft] = useState<ComposerDraft | null>(null);
  const [confirmation, setConfirmation] = useState<ComposerDraft | null>(null);

  useEffect(() => {
    setDraft(null);
    // An unconfirmed send must keep its original content and job for retry.
    if (!requestKey.current) setConfirmation(null);
  }, [open, jobId, shiftId]);

  const resetAndClose = () => {
    if (sendMutation.isLoading || requestKey.current) return;
    setDraft(null);
    setConfirmation(null);
    sendMutation.reset();
    onClose();
  };

  const handleSubmit = (values: FormValues) => {
    // Recipient count is fetched server-side in the confirmation view via
    // `useBroadcastPreview`. No client-side computation here — the server
    // shares its resolver between preview and commit, so the count the
    // user confirms is the count we deliver to.
    const nextDraft = {
      jobId,
      shiftId,
      values: { ...values, customUserIds: [...values.customUserIds] },
    };
    setDraft(nextDraft);
    setConfirmation(nextDraft);
  };

  const handleConfirm = (recipientCount: number) => {
    if (!confirmation) return;
    if (recipientCount <= 0) return;
    requestKey.current ??= crypto.randomUUID();
    sendMutation.mutate(
      {
        jobId: confirmation.jobId,
        body: {
          requestKey: requestKey.current,
          title: confirmation.values.title.trim(),
          body: confirmation.values.body.trim(),
          target: confirmation.values.target,
          customUserIds:
            confirmation.values.target === 'CUSTOM_USER_IDS'
              ? confirmation.values.customUserIds
              : undefined,
          replyBehavior: confirmation.values.replyBehavior,
        },
      },
      {
        onSuccess: (result) => {
          requestKey.current = null;
          setUnconfirmed(false);
          toast.success(
            `Posted to notifications for ${result.recipientCount} contractor${
              result.recipientCount === 1 ? '' : 's'
            }`
          );
          setDraft(null);
          setConfirmation(null);
          sendMutation.reset();
          onClose();
        },
        onError: (err) => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status && [400, 401, 403, 409].includes(status)) {
            requestKey.current = null;
            setUnconfirmed(false);
          } else {
            setUnconfirmed(true);
          }
          const message =
            err instanceof Error ? err.message : 'Failed to send broadcast';
          toast.error(message);
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth>
      {!confirmation ? (
        <Formik
          key={`${jobId}:${shiftId ?? ''}:${open}`}
          initialValues={
            draft?.jobId === jobId && draft.shiftId === shiftId
              ? draft.values
              : DEFAULT_VALUES
          }
          enableReinitialize
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            isValid,
            setFieldValue,
          }) => (
            <Form data-testid="broadcast-form">
              <DialogTitle>
                Send Announcement
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Job {jobId}
                  {shiftId ? ` · Shift ${shiftId}` : ''}
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    name="title"
                    label="Title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && (errors.title ?? `${values.title.length}/80`)}
                    fullWidth
                    inputProps={{
                      maxLength: 80,
                      'data-testid': 'broadcast-title',
                    }}
                  />
                  <TextField
                    name="body"
                    label="Message"
                    value={values.body}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.body && Boolean(errors.body)}
                    helperText={touched.body && (errors.body ?? `${values.body.length}/2000`)}
                    fullWidth
                    multiline
                    minRows={4}
                    inputProps={{
                      maxLength: 2000,
                      'data-testid': 'broadcast-body',
                    }}
                  />
                  <FormControl>
                    <FormLabel>Target</FormLabel>
                    <RadioGroup
                      name="target"
                      value={values.target}
                      onChange={(e) => setFieldValue('target', e.target.value)}
                      data-testid="broadcast-target"
                    >
                      <FormControlLabel
                        value="ALL_ASSIGNED"
                        control={<Radio />}
                        label="All Assigned"
                      />
                      <FormControlLabel
                        value="CONFIRMED_ONLY"
                        control={<Radio />}
                        label="Confirmed Only"
                      />
                      <FormControlLabel
                        value="NOT_CHECKED_IN"
                        control={<Radio />}
                        label="Not Checked In"
                      />
                      <FormControlLabel
                        value="CUSTOM_USER_IDS"
                        control={<Radio />}
                        label="Custom Selected"
                      />
                    </RadioGroup>
                  </FormControl>
                  {values.target === 'CUSTOM_USER_IDS' && (
                    <TextField
                      label="Comma-Separated User IDs"
                      value={values.customUserIds.join(',')}
                      onChange={(e) =>
                        setFieldValue(
                          'customUserIds',
                          e.target.value
                            .split(',')
                            .map((s) => Number(s.trim()))
                            .filter((n) => Number.isFinite(n) && n > 0)
                        )
                      }
                      helperText="Only assigned contractors are included."
                      fullWidth
                      inputProps={{ 'data-testid': 'broadcast-custom-ids' }}
                    />
                  )}
                  <FormControl>
                    <FormLabel>Reply Behavior</FormLabel>
                    <RadioGroup
                      name="replyBehavior"
                      value={values.replyBehavior}
                      onChange={(e) =>
                        setFieldValue('replyBehavior', e.target.value)
                      }
                      data-testid="broadcast-reply-behavior"
                    >
                      <FormControlLabel
                        value="READ_ONLY"
                        control={<Radio />}
                        label="Read-Only"
                      />
                      <FormControlLabel
                        value="REPLY_PRIVATELY_TO_LEAD"
                        control={<Radio />}
                        label="Reply Privately to Lead"
                      />
                    </RadioGroup>
                  </FormControl>
                  <Alert severity="info">
                    Attachments are not yet supported from the admin broadcast
                    composer — coming in a follow-up to issue #422.
                  </Alert>
                  <FormHelperText>
                    The next step previews the resolved recipient count before
                    sending.
                  </FormHelperText>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={resetAndClose}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isValid}
                  data-testid="broadcast-submit"
                >
                  Next: Review
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      ) : (
        <>
        {unconfirmed && <Alert severity="warning" sx={{ mx: 3 }}>
          The result is unconfirmed. Retry this exact announcement with the same request key before closing.
        </Alert>}
        <ConfirmationView
          jobId={confirmation.jobId}
          values={confirmation.values}
          onBack={() => setConfirmation(null)}
          onConfirm={handleConfirm}
          submitting={sendMutation.isLoading}
          unconfirmed={unconfirmed}
        />
        </>
      )}
    </Dialog>
  );
}

interface ConfirmationViewProps {
  unconfirmed: boolean;
  jobId: number;
  values: FormValues;
  onBack: () => void;
  onConfirm: (recipientCount: number) => void;
  /** True while the send mutation is in flight. */
  submitting: boolean;
}

function ConfirmationView({
  unconfirmed,
  jobId,
  values,
  onBack,
  onConfirm,
  submitting,
}: ConfirmationViewProps) {
  // Server-authoritative recipient count (issue #449). Replaces the
  // client-side `previewRecipientCount(roster, ...)` from the original
  // implementation — the server shares this resolver with the send path,
  // so the count shown here matches what the broadcast will actually
  // deliver to.
  const preview = useBroadcastPreview({
    jobId,
    target: values.target,
    customUserIds:
      values.target === 'CUSTOM_USER_IDS' ? values.customUserIds : undefined,
  });

  const recipientCount = preview.data?.recipientCount;
  const previewMessage = useMemo(() => {
    if (preview.isLoading) return 'Resolving recipient count…';
    if (preview.isError) {
      return 'Could not load recipient count — try again or send anyway.';
    }
    if (typeof recipientCount !== 'number') {
      return 'Recipient count unavailable.';
    }
    return `Send to ${recipientCount} contractor${
      recipientCount === 1 ? '' : 's'
    }`;
  }, [preview.isLoading, preview.isError, recipientCount]);

  const disableSend =
    submitting ||
    preview.isLoading ||
    preview.isError ||
    !(typeof recipientCount === 'number' && recipientCount > 0);

  return (
    <>
      <DialogTitle>Confirm Announcement</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Job
            </Typography>
            <Typography variant="body1">Job {jobId}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Title
            </Typography>
            <Typography variant="body1">{values.title}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Message
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {values.body}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Target
            </Typography>
            <Typography variant="body1">{values.target}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Reply Behavior
            </Typography>
            <Typography variant="body1">{values.replyBehavior}</Typography>
          </Box>
          <Alert
            severity={
              preview.isError
                ? 'error'
                : recipientCount === 0
                ? 'warning'
                : 'info'
            }
            data-testid="broadcast-preview-count"
          >
            {previewMessage}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack} disabled={submitting || unconfirmed}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(recipientCount ?? 0)}
          disabled={disableSend}
          startIcon={
            submitting || preview.isLoading ? (
              <CircularProgress size={16} />
            ) : undefined
          }
          data-testid="broadcast-confirm"
        >
          {submitting ? 'Sending…' : 'Send Announcement'}
        </Button>
      </DialogActions>
    </>
  );
}
